import { ICollaboratorEntity } from '../Services/api/collaborators/collaborators.interface'

const MENU_LABELS: Record<string, string> = {
  orders: 'Pedidos',
  collaborators: 'Colaboradores',
  customers: 'Clientes',
  products: 'Produtos',
  stocks: 'Estoque',
  production: 'Produção',
  financial: 'Financeiro',
  omiecompanies: 'Cadastro de Empresas',
  sellers: 'Vendedores',
  boxes: 'Caixas',
  cubages: 'Cubagem',
  hcp: 'HCP',
  replacementorders: 'Reposição',
  manualorders: 'Pedidos Manuais',
  productsservices: 'Serviços',
  categories: 'Categorias',
}

const FEATURE_LABELS: Array<{ match: (value: string) => boolean; label: string }> = [
  { match: (v) => v.includes('hcp-gestao-fluxo'), label: 'Gestão de fluxo' },
  { match: (v) => v.includes('hcp-arte'), label: 'Arte final' },
  { match: (v) => v.includes('hcp-montagem'), label: 'Montagem' },
  { match: (v) => v.includes('hcp-config'), label: 'Config. HCP' },
  { match: (v) => v.includes('hcp-colab'), label: 'Equipe HCP' },
  { match: (v) => v.includes('hcp-finalizar-card'), label: 'Finalizar card HCP' },
  { match: (v) => v.includes('orders-cancel'), label: 'Cancelar pedido' },
  { match: (v) => v.includes('orders-reprocess'), label: 'Reprocessar pedido' },
  { match: (v) => v.includes('orders-list-separation'), label: 'Lista separação' },
  { match: (v) => v.includes('adjust-manually'), label: 'Ajuste estoque' },
  { match: (v) => v.includes('products-deactivate'), label: 'Desativar produtos' },
]

function parsePermissionArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []

    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean).map(String)
      }
    } catch {
      return [trimmed]
    }
  }

  return []
}

type CollaboratorRaw = Partial<ICollaboratorEntity> & {
  permissions_screens?: unknown
  permissionScreens?: unknown
}

export function normalizeCollaboratorEntity(raw: CollaboratorRaw): ICollaboratorEntity {
  const source = raw as CollaboratorRaw & Record<string, unknown>

  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    username: String(raw.username ?? ''),
    permissions: parsePermissionArray(raw.permissions),
    permissionsScreens: parsePermissionArray(
      raw.permissionsScreens ?? source.permissions_screens ?? source.permissionScreens
    ),
  }
}

/** Chaves de permissão de telas (ignora `permissions: ['all']` da API). */
export function getCollaboratorScreenPermissionKeys(
  permissionsScreens: string[] = [],
  permissions: string[] = []
): string[] {
  const screens = (permissionsScreens ?? []).filter(Boolean)

  if (screens.length > 0) {
    return Array.from(new Set(screens))
  }

  return Array.from(new Set((permissions ?? []).filter((p) => p && p !== 'all')))
}

function normalizeMenuKey(value: string): string {
  return value.toLowerCase().replace(/-/g, '')
}

export function getCollaboratorPermissionLabel(perm: string): string | null {
  const normalized = perm.toLowerCase().trim()
  if (!normalized) return null

  if (normalized === 'all') return 'Admin geral'
  if (normalized === 'all:hub') return 'HUB completo'
  if (normalized === 'all:hcp') return 'HCP completo'

  const featureLabel = FEATURE_LABELS.find((item) => item.match(normalized))
  if (featureLabel) return featureLabel.label

  if (normalized.startsWith('menu:')) {
    const menuPart = normalized.replace(/^menu:/, '').replace(/:manage$/, '')
    const menuKey = normalizeMenuKey(menuPart)
    if (MENU_LABELS[menuKey]) return MENU_LABELS[menuKey]
  }

  if (normalized.startsWith('feature:')) {
    if (normalized.includes('hcp')) return 'HCP'
    return null
  }

  if (normalized.endsWith(':manage')) {
    const withoutManage = normalized.replace(/:manage$/, '')
    const lastSegment = withoutManage.split(':').pop() ?? withoutManage
    const legacyKey = normalizeMenuKey(lastSegment)
    if (MENU_LABELS[legacyKey]) return MENU_LABELS[legacyKey]

    if (withoutManage.startsWith('stocks:')) return 'Estoque'
    if (withoutManage.startsWith('orders:')) return 'Pedidos'
  }

  const includesFallback: Array<[string, string]> = [
    ['orders', 'Pedidos'],
    ['stocks', 'Estoque'],
    ['production', 'Produção'],
    ['financial', 'Financeiro'],
    ['omiecompanies', 'Cadastro de Empresas'],
    ['products', 'Produtos'],
    ['customers', 'Clientes'],
    ['boxes', 'Caixas'],
    ['sellers', 'Vendedores'],
    ['collaborators', 'Colaboradores'],
    ['hcp', 'HCP'],
    ['cubages', 'Cubagem'],
    ['categories', 'Categorias'],
  ]

  const includesMatch = includesFallback.find(([key]) => normalized.includes(key))
  if (includesMatch) return includesMatch[1]

  return null
}

export function getCollaboratorPermissionLabels(
  permissionsScreens: string[] = [],
  permissions: string[] = []
): string[] {
  const keys = getCollaboratorScreenPermissionKeys(permissionsScreens, permissions)

  return Array.from(
    new Set(
      keys
        .map(getCollaboratorPermissionLabel)
        .filter((label): label is string => Boolean(label))
    )
  )
}
